import React, { useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { ChatPage } from './pages/ChatPage';
import { ManagePlanPage } from './pages/ManagePlanPage';
import { WelcomePromotionPage } from './pages/WelcomePromotionPage';
import { AdminConsolePage } from './pages/admin/AdminConsolePage';
import { MonitorChatsPage } from './pages/admin/MonitorChatsPage';
import { SessionDetailPage } from './pages/admin/SessionDetailPage';
import { ManageCouponsPage } from './pages/admin/ManageCouponsPage';
import { ManageLlmProvidersPage } from './pages/admin/ManageLlmProvidersPage';
import { PoliciesIndexPage } from './pages/policies/PoliciesIndexPage';
import { TermsOfUsePage } from './pages/policies/TermsOfUsePage';
import { PrivacyPolicyPage } from './pages/policies/PrivacyPolicyPage';
import { DataProcessingAddendumPage } from './pages/policies/DataProcessingAddendumPage';
import { SmsOptInPolicyPage } from './pages/policies/SmsOptInPolicyPage';
import { useOTPAuth } from './hooks/useOTPAuth';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { ErrorConsole } from './components/ErrorConsole';
import { ConfigValidator } from './components/ConfigValidator';
import { PullToRefreshIndicator } from './components/PullToRefreshIndicator';

/**
 * Redirect to chat if already authenticated (but allow anonymous users to login)
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAnonymous, loading } = useOTPAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-12 w-12 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Only redirect to chat if user is authenticated AND not anonymous
  // Anonymous users should be able to access login page to upgrade their account
  if (isAuthenticated && !isAnonymous) {
    return <Navigate to="/chat" replace />;
  }

  return <>{children}</>;
};

/**
 * Protected route that requires full authentication (not anonymous)
 * Redirects to login with the current path as redirect parameter
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAnonymous, loading } = useOTPAuth();
  const location = window.location;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-12 w-12 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated or if user is anonymous
  if (!isAuthenticated || isAnonymous) {
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  return <>{children}</>;
};

/**
 * Protected route that requires admin.access permission
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAnonymous, user, loading } = useOTPAuth();
  const location = window.location;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-12 w-12 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated or anonymous, preserving the intended destination
  if (!isAuthenticated || isAnonymous) {
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Check if user has admin.access permission
  const hasAdminAccess = user?.permissions?.includes('admin.access') || false;

  if (!hasAdminAccess) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
          <button
            onClick={() => window.location.href = '/chat'}
            className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal-hover transition-colors"
          >
            Go to Chat
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

function App() {
  // Memoize to prevent unnecessary effect re-runs in usePullToRefresh
  const handleRefresh = useCallback(() => {
    // Clear service worker cache before reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }
  }, []);

  // Enable pull-to-refresh functionality for PWA
  // Helps users recover from blank pages after deployments
  // Requirements: touch must start in top 100px, swipe must go 1/3 of screen
  const pullToRefreshState = usePullToRefresh({
    startZoneHeight: 100, // Touch must start within top 100px of viewport
    thresholdFraction: 0.33, // Must swipe at least 1/3 of screen height
    maxPullDistance: 150,
    onRefresh: handleRefresh,
  });

  return (
    <>
      <ConfigValidator />
      <ErrorConsole />
      <PullToRefreshIndicator
        isPulling={pullToRefreshState.isPulling}
        pullDistance={pullToRefreshState.pullDistance}
        isRefreshing={pullToRefreshState.isRefreshing}
        progress={pullToRefreshState.progress}
      />
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/chat"
            element={<ChatPage />}
          />
          <Route
            path="/manage-plan"
            element={
              <ProtectedRoute>
                <ManagePlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotions/welcome"
            element={<WelcomePromotionPage />}
          />
          <Route
            path="/admin/console"
            element={
              <AdminRoute>
                <AdminConsolePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/monitor-chats"
            element={
              <AdminRoute>
                <MonitorChatsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/sessions/:sessionId"
            element={
              <AdminRoute>
                <SessionDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <AdminRoute>
                <ManageCouponsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/llm-providers"
            element={
              <AdminRoute>
                <ManageLlmProvidersPage />
              </AdminRoute>
            }
          />
          <Route path="/policies" element={<PoliciesIndexPage />} />
          <Route path="/policies/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/policies/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/policies/data-processing-addendum" element={<DataProcessingAddendumPage />} />
          <Route path="/policies/sms-opt-in" element={<SmsOptInPolicyPage />} />
          <Route path="/" element={<Navigate to="/chat" replace />} />
          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
